import clsx from 'clsx';
import {
  type FocusEvent,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';

import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';

import { RadioGroupContext } from './RadioGroupContext.js';
import * as styles from './styles.sty.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useFormControlState } from '../../core/hooks/useFormControlState.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { isDev } from '../../core/utils/is-node-environment.js';
import { withFormFieldMessageContextProvider } from '../forms-core/components/form-field/FormFieldMessageContextProvider.js';
import { InternalBaseItem as BaseItem } from '../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { useSyncedFormControlProps } from '../forms-core/hooks/useSyncedFormControlProps.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { useInputHint } from '../hooks/useInputHint.js';
import type { FormControlRef } from '../shared-types.js';

// #region Typings
/**
 * Properties for the RadioGroup component
 * @public
 */
export type RadioGroupProps = FormControlProps<
  string,
  (value: string, event?: FocusEvent<HTMLInputElement>) => void | never
> &
  StylingProps &
  WithChildren &
  DataTestId &
  MaskingProps;
// #endregion

const RadioGroupRoot = /* @__PURE__ */ forwardRef<
  FormControlRef,
  RadioGroupProps
>((props, forwardedRef) => {
  const {
    children,
    id: propId,
    disabled: propDisabled,
    value,
    name,
    defaultValue,
    controlState,
    onChange,
    readOnly = false,
    required: propRequired,
    'aria-disabled': ariaDisabledProp,
    'aria-describedby': ariaDescribedby,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  }: RadioGroupProps = props;

  const { disabled, required, id, ariaDisabled } = useSyncedFormControlProps({
    propDisabled,
    propRequired,
    propId,
    propAriaDisabled: ariaDisabledProp,
  });

  const { state: messagesContextState, dispatch: messagesContextDispatch } =
    useFormFieldMessagesContext();
  const [controlStateHasError, hint] = useFormControlState(controlState);

  const hasError = controlState
    ? controlStateHasError
    : messagesContextState.showError;

  // _useInputHint will be replaced with new validation concept. APPDEV-8298
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { inputProps, hintProps } = useInputHint({
    ariaDescribedby,
    hint,
    hasError,
  });

  const [controllableState, setControllableState] = useControllableState({
    defaultValue,
    value,
    onChange,
  });

  const wrapperRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(forwardedRef, (): FormControlRef<HTMLDivElement> => {
    return {
      element: wrapperRef.current,
      focus() {
        const checkedInput = wrapperRef.current?.querySelector(
          '[aria-checked="true"]',
        ) as HTMLInputElement;
        (
          checkedInput ??
          (wrapperRef.current?.querySelector('input') as HTMLInputElement)
        )?.focus();
      },
      validate() {
        if (controlState !== undefined) {
          return;
        }

        const inputs = wrapperRef.current?.querySelectorAll('input');
        inputs?.forEach((input) => {
          input?.checkValidity();
        });

        messagesContextDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
      },
    };
  });

  if (isDev() && name === undefined) {
    // eslint-disable-next-line no-console
    console.warn(
      'RadioGroup: The "name" prop is missing, which is necessary to link the radio buttons correctly.',
    );
  }

  return (
    <div
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      aria-orientation="vertical"
      role="radiogroup"
      aria-live="polite"
      aria-disabled={ariaDisabledProp}
      ref={wrapperRef}
      className={clsx(consumerClassName, styles.group, {
        [styles.readOnly]: readOnly,
      })}
      style={consumerStyle}
      id={id}
      {...inputProps}
      {...remainingProps}
    >
      <RadioGroupContext.Provider
        value={{
          name,
          isDisabled: disabled,
          ariaDisabled,
          hasError,
          value: controllableState,
          onChange: setControllableState,
          readOnly,
          required,
          controlState,
        }}
      >
        <Flex flexDirection="column" gap={8}>
          {children}
        </Flex>
      </RadioGroupContext.Provider>
      {hint && (
        <BaseItem
          variant={hasError ? 'error' : 'hint'}
          disabled={disabled || ariaDisabled}
          {...hintProps}
        >
          {hint}
        </BaseItem>
      )}
    </div>
  );
});

/**
 * Radio buttons allow users to select one option from
 * a group of related options. To group options, wrap the
 * `Radio` components inside a 'RadioGroup' component.
 * @public
 */
export const RadioGroup = withFormFieldMessageContextProvider<
  RadioGroupProps,
  FormControlRef
>(RadioGroupRoot);

(RadioGroup as typeof RadioGroup & { displayName: string }).displayName =
  'RadioGroup';
